import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { PageLayout } from "@/components/layout/Sidebar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { 
  UserPlus, 
  Trash2, 
  Users,
  Edit,
  History,
  User,
  Upload,
  Loader2,
  Camera,
  X,
  Search,
  Plus,
  Swords,
  Shield,
  TrendingUp,
  UserCheck,
  Clock,
  CheckCircle,
  XCircle,
  Mail,
  Phone,
  Calendar
} from "lucide-react";
import { getPhotoUrl } from "@/utils/photoUrl";
import { getAvatarColor, getInitials } from "@/utils/avatar";
import { useLanguage } from "@/i18n";

const POSITIONS = ["Levantador", "Ponteiro", "Oposto", "Central", "Líbero"];
const YANKEE_OPTIONS = ["C-", "C", "C+", "B-", "B", "B+", "A-", "A", "A+"];

export default function PlayersPage({ evaluator, onLogout }) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [players, setPlayers] = useState([]);
  const [pendingPlayers, setPendingPlayers] = useState([]);
  const [playerAverages, setPlayerAverages] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newPlayer, setNewPlayer] = useState({ first_name: "", last_name: "", position: "", position2: "", yankee: "", photo_url: "" });
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [positionFilter, setPositionFilter] = useState("all");
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUploadingEdit, setIsUploadingEdit] = useState(false);
  const [isUploadingNew, setIsUploadingNew] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [activeTab, setActiveTab] = useState("approved"); // "approved" or "pending"
  const [approvingId, setApprovingId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  
  // Modal para tornar avaliador
  const [makeEvaluatorOpen, setMakeEvaluatorOpen] = useState(false);
  const [selectedPlayerForEvaluator, setSelectedPlayerForEvaluator] = useState(null);
  const [evaluatorEmail, setEvaluatorEmail] = useState("");
  const [evaluatorPassword, setEvaluatorPassword] = useState("");
  const [evaluatorRole, setEvaluatorRole] = useState("avaliador");
  const [isMakingEvaluator, setIsMakingEvaluator] = useState(false);
  const [isDeletingHistory, setIsDeletingHistory] = useState(null);
  const [togglingMemberType, setTogglingMemberType] = useState(null);
  
  // Clear old players
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [oldPlayersInfo, setOldPlayersInfo] = useState(null);
  const [isClearing, setIsClearing] = useState(false);
  const [selectedPlayersToDelete, setSelectedPlayersToDelete] = useState([]);
  
  const [memberTypeFilter, setMemberTypeFilter] = useState("all");
  
  // Permission to edit players
  const [canEditPlayers, setCanEditPlayers] = useState(true);

  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [playersRes, averagesRes, pendingRes] = await Promise.all([
        axios.get(`${API}/players`),
        axios.get(`${API}/players/all/averages`),
        evaluator?.role === 'admin' ? axios.get(`${API}/players/pending`) : Promise.resolve({ data: [] })
      ]);
      // Filter out pending players from main list
      const approvedPlayers = playersRes.data.filter(p => p.approved !== false);
      setPlayers(approvedPlayers);
      setPendingPlayers(pendingRes.data || []);
      
      // Create a map of player averages by player_id
      const avgMap = {};
      averagesRes.data.forEach(p => {
        avgMap[p.player_id] = p;
      });
      setPlayerAverages(avgMap);
      
      // Fetch edit permission for non-admin evaluators
      if (evaluator?.role !== 'admin' && evaluator?.id) {
        try {
          const permsRes = await axios.get(`${API}/evaluator-permissions/${evaluator.id}`);
          setCanEditPlayers(permsRes.data.can_edit_players !== false);
        } catch (err) {
          setCanEditPlayers(true); // Default to true if error
        }
      } else if (evaluator?.role === 'admin') {
        setCanEditPlayers(true); // Admin always can edit
      }
    } catch (error) {
      toast.error(t('toasts.errorLoadingPlayers'));
    } finally {
      setIsLoading(false);
    }
  };

  const getPlayerName = (p) => p.first_name ? `${p.first_name} ${p.last_name}` : p.name;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newPlayer.first_name || !newPlayer.last_name || !newPlayer.position) {
      toast.error(t('toasts.fillNamePositon'));
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/players`, newPlayer);
      toast.success(t('toasts.playerAdded'));
      setNewPlayer({ first_name: "", last_name: "", position: "", position2: "", yankee: "", photo_url: "" });
      setShowAddForm(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('toasts.errorAddingPlayer'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!editingPlayer) return;
    setIsSubmitting(true);
    try {
      await axios.put(`${API}/players/${editingPlayer.id}`, editingPlayer);
      toast.success(t('toasts.playerUpdated'));
      setEditDialogOpen(false);
      setEditingPlayer(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('toasts.errorUpdatingPlayer'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeletePlayer = async (playerId, playerName) => {
    if (!confirm(`${t('common.delete')} ${playerName}?`)) return;
    try {
      await axios.delete(`${API}/players/${playerId}`);
      toast.success(t('toasts.playerDeleted'));
      fetchData();
    } catch (error) {
      toast.error(t('toasts.errorDeletingPlayer'));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedPlayers.length === 0) return;
    setIsDeleting(true);
    try {
      await axios.delete(`${API}/players/bulk`, { data: { player_ids: selectedPlayers } });
      toast.success(`${selectedPlayers.length} ${t('toasts.playersDeleted')}`);
      setSelectedPlayers([]);
      fetchData();
    } catch (error) {
      toast.error(t('toasts.errorDeletingPlayer'));
    } finally {
      setIsDeleting(false);
    }
  };

  // Check old players before clearing
  const handleCheckOldPlayers = async () => {
    try {
      const res = await axios.get(`${API}/admin/players-summary?include_all=true`);
      setOldPlayersInfo(res.data);
      setSelectedPlayersToDelete([]); // Reset selection
      setClearDialogOpen(true);
    } catch (error) {
      toast.error(t('toasts.errorCheckingPlayers'));
    }
  };

  // Clear selected players
  const handleClearSelectedPlayers = async () => {
    if (selectedPlayersToDelete.length === 0) return;
    
    setIsClearing(true);
    try {
      const res = await axios.post(`${API}/admin/clear-selected-players`, {
        player_ids: selectedPlayersToDelete
      });
      toast.success(res.data.message);
      setClearDialogOpen(false);
      setOldPlayersInfo(null);
      setSelectedPlayersToDelete([]);
      fetchData(); // Refresh player list
    } catch (error) {
      toast.error(t('toasts.errorClearingPlayers'));
    } finally {
      setIsClearing(false);
    }
  };

  // Clear old players (all at once)
  const handleClearOldPlayers = async () => {
    setIsClearing(true);
    try {
      const res = await axios.delete(`${API}/admin/clear-old-players`);
      toast.success(res.data.message);
      setClearDialogOpen(false);
      setOldPlayersInfo(null);
      fetchData(); // Refresh player list
    } catch (error) {
      toast.error(t('toasts.errorClearingPlayers'));
    } finally {
      setIsClearing(false);
    }
  };

  const handlePhotoUpload = async (e, isEdit = false) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error(t('toasts.useOnlyImages'));
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error(t('toasts.maxFileSize'));
      return;
    }
    
    isEdit ? setIsUploadingEdit(true) : setIsUploadingNew(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      // Use Cloudinary endpoint for persistent storage
      const res = await axios.post(`${API}/upload/photo/cloudinary`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (isEdit) {
        setEditingPlayer({ ...editingPlayer, photo_url: res.data.photo_url });
      } else {
        setNewPlayer({ ...newPlayer, photo_url: res.data.photo_url });
      }
      toast.success(t('toasts.photoUploaded'));
    } catch (error) {
      toast.error(t('toasts.errorPhotoUpload'));
    } finally {
      isEdit ? setIsUploadingEdit(false) : setIsUploadingNew(false);
    }
  };

  const openMakeEvaluatorModal = (player) => {
    setSelectedPlayerForEvaluator(player);
    setEvaluatorEmail(player.email || "");
    setEvaluatorPassword("");
    setMakeEvaluatorOpen(true);
  };

  const handleMakeEvaluator = async () => {
    if (!evaluatorEmail || !evaluatorPassword) {
      toast.error(t('toasts.fillEmailPassword'));
      return;
    }

    setIsMakingEvaluator(true);
    try {
      await axios.post(`${API}/players/${selectedPlayerForEvaluator.id}/make-evaluator`, {
        email: evaluatorEmail,
        password: evaluatorPassword,
        name: getPlayerName(selectedPlayerForEvaluator),
        role: evaluatorRole
      });
      toast.success(`${getPlayerName(selectedPlayerForEvaluator)} ${t('toasts.madeEvaluator')} ${evaluatorRole} ${t('toasts.successfully')}`);
      setMakeEvaluatorOpen(false);
      setEvaluatorRole("avaliador");
    } catch (error) {
      toast.error(error.response?.data?.detail || t('toasts.errorMakingEvaluator'));
    } finally {
      setIsMakingEvaluator(false);
    }
  };

  const handleDeleteHistory = async (playerId, playerName) => {
    if (!window.confirm(`${t('common.confirm')} - ${t('players.history')} ${playerName}?`)) {
      return;
    }
    
    setIsDeletingHistory(playerId);
    try {
      const response = await axios.delete(`${API}/players/${playerId}/history`);
      toast.success(t('toasts.historyDeleted'));
      fetchData(); // Refresh to update averages
    } catch (error) {
      toast.error(error.response?.data?.detail || t('toasts.errorDeletingHistory'));
    } finally {
      setIsDeletingHistory(null);
    }
  };

  const handleApprovePlayer = async (playerId) => {
    setApprovingId(playerId);
    try {
      await axios.post(`${API}/players/${playerId}/approve`);
      toast.success(t('toasts.playerApproved'));
      // Refresh data
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('toasts.errorApproving'));
    } finally {
      setApprovingId(null);
    }
  };

  const handleRejectPlayer = async (playerId) => {
    setRejectingId(playerId);
    try {
      await axios.post(`${API}/players/${playerId}/reject`);
      toast.success(t('toasts.playerRejected'));
      // Refresh data
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('toasts.errorRejecting'));
    } finally {
      setRejectingId(null);
    }
  };

  const toggleSelectAll = () => {
    if (selectedPlayers.length === filteredPlayers.length) {
      setSelectedPlayers([]);
    } else {
      setSelectedPlayers(filteredPlayers.map(p => p.id));
    }
  };

  const handleToggleMemberType = async (playerId, currentType) => {
    setTogglingMemberType(playerId);
    const newType = currentType === "membro" ? "convidado" : "membro";
    try {
      await axios.patch(`${API}/players/${playerId}/member-type`, { member_type: newType });
      toast.success(newType === "membro" ? "Marcado como Membro do Grupo" : "Marcado como Convidado");
      setPlayers(prev => prev.map(p => p.id === playerId ? { ...p, member_type: newType } : p));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao alterar tipo");
    } finally {
      setTogglingMemberType(null);
    }
  };

  const filteredPlayers = players
    .filter(p => positionFilter === "all" || p.position === positionFilter)
    .filter(p => memberTypeFilter === "all" || (p.member_type || "membro") === memberTypeFilter)
    .filter(p => {
      if (!searchQuery) return true;
      const name = getPlayerName(p).toLowerCase();
      return name.includes(searchQuery.toLowerCase());
    });

  return (
    <PageLayout evaluator={evaluator} onLogout={onLogout} title="Jogadores" subtitle="Gerencie os jogadores do time">
      {/* Tabs for Admin - Approved/Pending */}
      {evaluator?.role === 'admin' && (
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab("approved")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === "approved"
                ? "bg-[#CCFF00] text-black"
                : "bg-slate-800/50 text-slate-400 hover:bg-slate-800"
            }`}
          >
            <UserCheck className="h-4 w-4" />
            Aprovados ({players.length})
          </button>
          <button
            onClick={() => setActiveTab("pending")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === "pending"
                ? "bg-orange-500 text-white"
                : "bg-slate-800/50 text-slate-400 hover:bg-slate-800"
            }`}
          >
            <Clock className="h-4 w-4" />
            Pendentes ({pendingPlayers.length})
            {pendingPlayers.length > 0 && (
              <span className="ml-1 bg-red-500 text-white text-xs rounded-full px-2 py-0.5 animate-pulse">
                Novo
              </span>
            )}
          </button>
        </div>
      )}

      {/* Pending Players Tab */}
      {activeTab === "pending" && evaluator?.role === 'admin' && (
        <div className="space-y-4">
          {pendingPlayers.length === 0 ? (
            <Card className="bg-card border-border">
              <CardContent className="p-8 text-center">
                <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-3" />
                <p className="text-muted-foreground">Nenhum jogador aguardando aprovação</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {pendingPlayers.map((player) => (
                <Card key={player.id} className="bg-card border-border border-l-4 border-l-orange-500">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <div 
                          className="w-14 h-14 rounded-full flex items-center justify-center text-white font-bold text-lg"
                          style={{ backgroundColor: getAvatarColor(`${player.first_name} ${player.last_name}`) }}
                        >
                          {getInitials(`${player.first_name} ${player.last_name}`)}
                        </div>
                        <div>
                          <h3 className="font-bold text-foreground text-lg">
                            {player.first_name} {player.last_name}
                          </h3>
                          <Badge variant="outline" className="mt-1">{player.position}</Badge>
                          <div className="flex flex-wrap gap-3 mt-2 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Mail className="h-3 w-3" />
                              {player.email}
                            </span>
                            {player.phone && (
                              <span className="flex items-center gap-1">
                                <Phone className="h-3 w-3" />
                                {player.phone}
                              </span>
                            )}
                            {player.birth_date && (
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {new Date(player.birth_date).toLocaleDateString('pt-BR')}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            Cadastrado em: {new Date(player.created_at).toLocaleDateString('pt-BR')}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleApprovePlayer(player.id)}
                          disabled={approvingId === player.id}
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          {approvingId === player.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Aprovar
                            </>
                          )}
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="destructive"
                              disabled={rejectingId === player.id}
                            >
                              {rejectingId === player.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <>
                                  <XCircle className="h-4 w-4 mr-2" />
                                  Rejeitar
                                </>
                              )}
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-slate-900 border-white/10">
                            <AlertDialogHeader>
                              <AlertDialogTitle>Rejeitar cadastro?</AlertDialogTitle>
                              <AlertDialogDescription>
                                O cadastro de {player.first_name} {player.last_name} será removido e ele receberá um email informando.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleRejectPlayer(player.id)}>
                                Rejeitar
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Approved Players Tab */}
      {activeTab === "approved" && (
        <>
      {/* Action Bar */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="flex-1 min-w-[200px] max-w-md relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input 
            placeholder="Buscar jogador..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-slate-900/50 border-white/10 focus:border-primary"
          />
        </div>
        
        <Select value={positionFilter} onValueChange={setPositionFilter}>
          <SelectTrigger className="w-40 bg-slate-900/50 border-white/10">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas Posições</SelectItem>
            {POSITIONS.map(pos => (
              <SelectItem key={pos} value={pos}>{pos}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {evaluator?.role === 'admin' && (
          <Select value={memberTypeFilter} onValueChange={setMemberTypeFilter}>
            <SelectTrigger data-testid="member-type-filter" className="w-40 bg-slate-900/50 border-white/10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="membro">Membros</SelectItem>
              <SelectItem value="convidado">Convidados</SelectItem>
            </SelectContent>
          </Select>
        )}

        <Button 
          onClick={() => setShowAddForm(true)}
          className="bg-[#CCFF00] text-black font-bold hover:bg-[#CCFF00]/90 shadow-[0_0_15px_rgba(204,255,0,0.3)]"
        >
          <Plus className="h-4 w-4 mr-2" />
          Adicionar
        </Button>

        {/* Clear Old Players Button - Admin Only */}
        {evaluator?.role === 'admin' && (
          <AlertDialog open={clearDialogOpen} onOpenChange={setClearDialogOpen}>
            <AlertDialogTrigger asChild>
              <Button 
                variant="outline" 
                className="border-orange-500/50 text-orange-400 hover:bg-orange-500/20 hover:text-orange-300"
                onClick={handleCheckOldPlayers}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Limpar Dados
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-slate-900 border-white/10 max-w-2xl">
              <AlertDialogHeader>
                <AlertDialogTitle className="text-orange-400">⚠️ Limpar Jogadores e Dados</AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="text-slate-300">
                    {oldPlayersInfo ? (
                      <div className="space-y-4">
                        <div className="flex gap-4 text-sm">
                          <div className="flex-1 bg-red-500/10 rounded-lg p-3 text-center">
                            <p className="text-2xl font-bold text-red-400">{oldPlayersInfo.old_count}</p>
                            <p className="text-xs text-slate-400">Jogadores antigos</p>
                          </div>
                          <div className="flex-1 bg-green-500/10 rounded-lg p-3 text-center">
                            <p className="text-2xl font-bold text-green-400">{oldPlayersInfo.today_count}</p>
                            <p className="text-xs text-slate-400">Jogadores de hoje</p>
                          </div>
                        </div>
                        
                        {oldPlayersInfo.old_players?.length > 0 && (
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <p className="text-xs text-slate-400">Selecione jogadores para excluir:</p>
                              <div className="flex gap-2">
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  className="text-xs h-7"
                                  onClick={() => setSelectedPlayersToDelete(oldPlayersInfo.old_players.map(p => p.id))}
                                >
                                  Selecionar Todos
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  className="text-xs h-7"
                                  onClick={() => setSelectedPlayersToDelete([])}
                                >
                                  Limpar Seleção
                                </Button>
                              </div>
                            </div>
                            <div className="max-h-60 overflow-y-auto bg-slate-800/50 rounded-lg p-2 space-y-1">
                              {oldPlayersInfo.old_players.map((p) => (
                                <label 
                                  key={p.id} 
                                  className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                                    selectedPlayersToDelete.includes(p.id) ? 'bg-red-500/20' : 'hover:bg-slate-700/50'
                                  }`}
                                >
                                  <input 
                                    type="checkbox" 
                                    checked={selectedPlayersToDelete.includes(p.id)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedPlayersToDelete(prev => [...prev, p.id]);
                                      } else {
                                        setSelectedPlayersToDelete(prev => prev.filter(id => id !== p.id));
                                      }
                                    }}
                                    className="w-4 h-4 rounded border-slate-600"
                                  />
                                  <div className="flex-1">
                                    <p className="text-sm text-white">{p.name}</p>
                                    <p className="text-xs text-slate-500">{p.created_at ? new Date(p.created_at).toLocaleDateString('pt-BR') : 'Sem data'}</p>
                                  </div>
                                </label>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        <p className="text-red-400 font-semibold text-sm">⚠️ Esta ação não pode ser desfeita! As avaliações também serão excluídas.</p>
                      </div>
                    ) : (
                      <p>Carregando informações...</p>
                    )}
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction 
                  onClick={handleClearSelectedPlayers} 
                  disabled={isClearing || selectedPlayersToDelete.length === 0}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {isClearing ? "Limpando..." : `Excluir ${selectedPlayersToDelete.length} Jogador(es)`}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}

        {selectedPlayers.length > 0 && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" className="shadow-[0_0_15px_rgba(239,68,68,0.3)]">
                <Trash2 className="h-4 w-4 mr-2" />
                Excluir ({selectedPlayers.length})
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-slate-900 border-white/10">
              <AlertDialogHeader>
                <AlertDialogTitle>Excluir {selectedPlayers.length} jogadores?</AlertDialogTitle>
                <AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={handleBulkDelete} disabled={isDeleting}>
                  {isDeleting ? "Excluindo..." : "Excluir"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      {/* Add Player Form Modal */}
      <Dialog open={showAddForm} onOpenChange={setShowAddForm}>
        <DialogContent className="bg-slate-900 border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <UserPlus className="h-5 w-5 text-[#CCFF00]" />
              Adicionar Jogador
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-400">Nome</Label>
                <Input value={newPlayer.first_name} onChange={(e) => setNewPlayer({ ...newPlayer, first_name: e.target.value })} className="bg-slate-800/50 border-white/10" required />
              </div>
              <div>
                <Label className="text-xs text-slate-400">Sobrenome</Label>
                <Input value={newPlayer.last_name} onChange={(e) => setNewPlayer({ ...newPlayer, last_name: e.target.value })} className="bg-slate-800/50 border-white/10" required />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-400">Posição</Label>
                <Select value={newPlayer.position} onValueChange={(v) => setNewPlayer({ ...newPlayer, position: v })}>
                  <SelectTrigger className="bg-slate-800/50 border-white/10"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>{POSITIONS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-400">2ª Posição</Label>
                <Select value={newPlayer.position2} onValueChange={(v) => setNewPlayer({ ...newPlayer, position2: v })}>
                  <SelectTrigger className="bg-slate-800/50 border-white/10"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>{POSITIONS.filter(p => p !== newPlayer.position).map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-400">Nível</Label>
              <Select value={newPlayer.yankee || "none"} onValueChange={(v) => setNewPlayer({ ...newPlayer, yankee: v === "none" ? "" : v })}>
                <SelectTrigger className="bg-slate-800/50 border-white/10"><SelectValue placeholder="Nenhum" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Nenhum</SelectItem>{YANKEE_OPTIONS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            
            {/* Photo Upload */}
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-slate-800 flex items-center justify-center overflow-hidden border border-white/10">
                {newPlayer.photo_url ? (
                  <img src={newPlayer.photo_url} alt="Preview" className="w-full h-full object-cover" />
                ) : (
                  <Camera className="h-6 w-6 text-slate-500" />
                )}
              </div>
              <div className="flex-1 flex flex-col gap-2">
                {/* Botão para galeria */}
                <label className="cursor-pointer">
                  <div className={`flex items-center justify-center gap-2 h-10 px-4 rounded-lg border border-dashed transition-colors ${isUploadingNew ? 'bg-primary/10 border-primary' : 'border-white/20 hover:border-primary'}`}>
                    {isUploadingNew ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    <span className="text-sm">{isUploadingNew ? "Enviando..." : "Escolher da Galeria"}</span>
                  </div>
                  <input 
                    type="file" 
                    accept="image/jpeg,image/png,image/gif,image/webp,image/*" 
                    className="hidden" 
                    onChange={(e) => handlePhotoUpload(e, false)} 
                    disabled={isUploadingNew} 
                  />
                </label>
                {/* Botão para câmera (mobile) */}
                <label className="cursor-pointer">
                  <div className={`flex items-center justify-center gap-2 h-10 px-4 rounded-lg border border-dashed transition-colors ${isUploadingNew ? 'bg-primary/10 border-primary' : 'border-white/20 hover:border-primary'}`}>
                    {isUploadingNew ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
                    <span className="text-sm">{isUploadingNew ? "Enviando..." : "Tirar Foto"}</span>
                  </div>
                  <input 
                    type="file" 
                    accept="image/*" 
                    capture="environment"
                    className="hidden" 
                    onChange={(e) => handlePhotoUpload(e, false)} 
                    disabled={isUploadingNew} 
                  />
                </label>
              </div>
            </div>
            
            <Button type="submit" className="w-full bg-[#CCFF00] text-black font-bold hover:bg-[#CCFF00]/90" disabled={isSubmitting}>
              {isSubmitting ? "Adicionando..." : "Adicionar Jogador"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Players Grid */}
      {isLoading ? (
        <div className="text-center py-12 text-slate-400">Carregando...</div>
      ) : filteredPlayers.length === 0 ? (
        <Card className="bg-slate-950/40 backdrop-blur-md border border-white/10 rounded-xl">
          <CardContent className="py-12 text-center">
            <Users className="h-12 w-12 text-slate-500 mx-auto mb-4" />
            <p className="text-slate-400">Nenhum jogador encontrado</p>
            <Button onClick={() => setShowAddForm(true)} className="mt-4 bg-primary">
              <Plus className="h-4 w-4 mr-2" /> Adicionar Primeiro Jogador
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Select All */}
          <div className="flex items-center gap-2 mb-4">
            <Checkbox 
              checked={selectedPlayers.length === filteredPlayers.length && filteredPlayers.length > 0}
              onCheckedChange={toggleSelectAll}
            />
            <span className="text-sm text-slate-400">
              {selectedPlayers.length > 0 ? `${selectedPlayers.length} selecionados` : "Selecionar todos"}
            </span>
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredPlayers.map((player, idx) => (
              <Card 
                key={player.id}
                className={`bg-slate-950/40 backdrop-blur-md border rounded-xl overflow-hidden transition-all duration-300 hover:translate-y-[-2px] hover:shadow-xl group ${
                  selectedPlayers.includes(player.id) ? 'border-primary shadow-[0_0_20px_rgba(0,122,255,0.2)]' : 'border-white/10 hover:border-white/20'
                }`}
              >
                <CardContent className="p-0">
                  {/* Photo Section */}
                  <div className="relative h-32 bg-gradient-to-b from-slate-800/50 to-transparent flex items-center justify-center">
                    {player.photo_url ? (
                      <img 
                        src={getPhotoUrl(player.photo_url)}
                        alt={getPlayerName(player)}
                        className="w-24 h-24 rounded-full object-cover border-4 border-slate-900 shadow-xl"
                        onError={(e) => {
                          // Hide broken image and show avatar
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div 
                      className={`w-24 h-24 rounded-full flex items-center justify-center border-4 border-slate-900 shadow-xl ${player.photo_url ? 'hidden' : 'flex'}`}
                      style={{ backgroundColor: getAvatarColor(getPlayerName(player)) }}
                    >
                      <span className="text-2xl font-bold text-white drop-shadow-lg">
                        {getInitials(player.first_name, player.last_name)}
                      </span>
                    </div>
                    
                    {/* Checkbox */}
                    <div className="absolute top-3 left-3">
                      <Checkbox 
                        checked={selectedPlayers.includes(player.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedPlayers([...selectedPlayers, player.id]);
                          } else {
                            setSelectedPlayers(selectedPlayers.filter(id => id !== player.id));
                          }
                        }}
                        className="bg-slate-900/80 border-white/20"
                      />
                    </div>
                    
                    {/* Actions */}
                    <div className="absolute top-3 right-3 flex gap-1">
                      {canEditPlayers && (
                        <Dialog open={editDialogOpen && editingPlayer?.id === player.id} onOpenChange={(open) => {
                          setEditDialogOpen(open);
                          if (!open) setEditingPlayer(null);
                        }}>
                          <DialogTrigger asChild>
                            <button 
                              onClick={() => setEditingPlayer(player)}
                              className="p-2 bg-slate-900/80 rounded-lg hover:bg-primary transition-colors"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          </DialogTrigger>
                          <DialogContent className="bg-slate-900 border-white/10 max-w-md">
                            <DialogHeader>
                              <DialogTitle className="flex items-center gap-2 text-white">
                                <Edit className="h-5 w-5 text-primary" />
                                Editar Jogador
                              </DialogTitle>
                            </DialogHeader>
                            {editingPlayer && (
                              <form onSubmit={handleEdit} className="space-y-4">
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <Label className="text-xs text-slate-400">Nome</Label>
                                    <Input value={editingPlayer.first_name || ""} onChange={(e) => setEditingPlayer({ ...editingPlayer, first_name: e.target.value })} className="bg-slate-800/50 border-white/10" />
                                  </div>
                                  <div>
                                    <Label className="text-xs text-slate-400">Sobrenome</Label>
                                    <Input value={editingPlayer.last_name || ""} onChange={(e) => setEditingPlayer({ ...editingPlayer, last_name: e.target.value })} className="bg-slate-800/50 border-white/10" />
                                  </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <Label className="text-xs text-slate-400">Posição</Label>
                                    <Select value={editingPlayer.position} onValueChange={(v) => setEditingPlayer({ ...editingPlayer, position: v })}>
                                      <SelectTrigger className="bg-slate-800/50 border-white/10"><SelectValue /></SelectTrigger>
                                      <SelectContent>{POSITIONS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                                    </Select>
                                  </div>
                                  <div>
                                    <Label className="text-xs text-slate-400">2ª Posição</Label>
                                    <Select value={editingPlayer.position2 || ""} onValueChange={(v) => setEditingPlayer({ ...editingPlayer, position2: v })}>
                                      <SelectTrigger className="bg-slate-800/50 border-white/10"><SelectValue placeholder="Nenhuma" /></SelectTrigger>
                                      <SelectContent>{POSITIONS.filter(p => p !== editingPlayer.position).map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                                    </Select>
                                  </div>
                                </div>
                                <div>
                                  <Label className="text-xs text-slate-400">Nível</Label>
                                  <Select value={editingPlayer.yankee || "none"} onValueChange={(v) => setEditingPlayer({ ...editingPlayer, yankee: v === "none" ? "" : v })}>
                                    <SelectTrigger className="bg-slate-800/50 border-white/10"><SelectValue /></SelectTrigger>
                                    <SelectContent><SelectItem value="none">Nenhum</SelectItem>{YANKEE_OPTIONS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                                  </Select>
                                </div>
                                
                                {/* Photo Upload */}
                                <div className="flex items-center gap-4">
                                  <div className="relative group">
                                    <div className="w-16 h-16 rounded-full bg-slate-800 flex items-center justify-center overflow-hidden border border-white/10">
                                    {editingPlayer.photo_url ? (
                                      <img src={getPhotoUrl(editingPlayer.photo_url)} alt="Preview" className="w-full h-full object-cover" />
                                    ) : (
                                      <Camera className="h-6 w-6 text-slate-500" />
                                    )}
                                  </div>
                                  {editingPlayer.photo_url && (
                                    <button type="button" onClick={() => setEditingPlayer({ ...editingPlayer, photo_url: "" })} className="absolute -top-1 -right-1 bg-red-500 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                      <X className="h-3 w-3" />
                                    </button>
                                  )}
                                </div>
                                <div className="flex-1 flex flex-col gap-2">
                                  {/* Galeria */}
                                  <label className="cursor-pointer">
                                    <div className={`flex items-center justify-center gap-2 h-9 px-3 rounded-lg border border-dashed transition-colors text-xs ${isUploadingEdit ? 'bg-primary/10 border-primary' : 'border-white/20 hover:border-primary'}`}>
                                      {isUploadingEdit ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                                      <span>{isUploadingEdit ? "Enviando..." : "Galeria"}</span>
                                    </div>
                                    <input 
                                      type="file" 
                                      accept="image/jpeg,image/png,image/gif,image/webp,image/*" 
                                      className="hidden" 
                                      onChange={(e) => handlePhotoUpload(e, true)} 
                                      disabled={isUploadingEdit} 
                                    />
                                  </label>
                                  {/* Câmera */}
                                  <label className="cursor-pointer">
                                    <div className={`flex items-center justify-center gap-2 h-9 px-3 rounded-lg border border-dashed transition-colors text-xs ${isUploadingEdit ? 'bg-primary/10 border-primary' : 'border-white/20 hover:border-primary'}`}>
                                      {isUploadingEdit ? <Loader2 className="h-3 w-3 animate-spin" /> : <Camera className="h-3 w-3" />}
                                      <span>{isUploadingEdit ? "Enviando..." : "Câmera"}</span>
                                    </div>
                                    <input 
                                      type="file" 
                                      accept="image/*" 
                                      capture="environment"
                                      className="hidden" 
                                      onChange={(e) => handlePhotoUpload(e, true)} 
                                      disabled={isUploadingEdit} 
                                    />
                                  </label>
                                </div>
                              </div>
                              
                              <Button type="submit" className="w-full bg-primary" disabled={isSubmitting || isUploadingEdit}>
                                {isSubmitting ? "Salvando..." : "Salvar"}
                              </Button>
                            </form>
                          )}
                        </DialogContent>
                      </Dialog>
                      )}
                      
                      <button 
                        onClick={() => navigate(`/players/${player.id}/history`)}
                        className="p-2 bg-slate-900/80 rounded-lg hover:bg-primary transition-colors"
                        title="Histórico"
                      >
                        <History className="h-4 w-4" />
                      </button>
                      
                      {evaluator?.role === 'admin' && (
                        <button 
                          onClick={() => openMakeEvaluatorModal(player)}
                          className="p-2 bg-slate-900/80 rounded-lg hover:bg-green-600 transition-colors"
                          title="Tornar Avaliador/Técnico"
                        >
                          <UserCheck className="h-4 w-4" />
                        </button>
                      )}
                      
                      {/* Apagar Histórico */}
                      {evaluator?.role === 'admin' && playerAverages[player.id]?.total_evaluations > 0 && (
                        <button 
                          onClick={() => handleDeleteHistory(player.id, getPlayerName(player))}
                          disabled={isDeletingHistory === player.id}
                          className="p-2 bg-slate-900/80 rounded-lg hover:bg-orange-500 transition-colors"
                          title="Apagar Histórico"
                        >
                          {isDeletingHistory === player.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <XCircle className="h-4 w-4" />
                          )}
                        </button>
                      )}
                      
                      <button 
                        onClick={() => handleDeletePlayer(player.id, getPlayerName(player))}
                        className="p-2 bg-slate-900/80 rounded-lg hover:bg-red-500 transition-colors"
                        title="Excluir Jogador"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Info Section */}
                  <div className="p-4">
                    <h3 className="font-bold text-white text-lg truncate text-center">{getPlayerName(player)}</h3>
                    <div className="flex items-center justify-center gap-2 mt-2">
                      <Badge variant="outline" className="text-xs border-white/20">{player.position}</Badge>
                      {player.position2 && <Badge variant="outline" className="text-xs border-white/10 text-slate-400">{player.position2}</Badge>}
                    </div>
                    {player.yankee && (
                      <div className="text-center mt-2">
                        <Badge className="bg-primary/20 text-primary border-0">{player.yankee}</Badge>
                      </div>
                    )}
                    
                    {/* Member Type Toggle - Admin Only */}
                    {evaluator?.role === 'admin' && (
                      <div className="flex justify-center mt-3">
                        <button
                          data-testid={`member-type-toggle-${player.id}`}
                          onClick={() => handleToggleMemberType(player.id, player.member_type || 'membro')}
                          disabled={togglingMemberType === player.id}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                            (player.member_type || 'membro') === 'membro'
                              ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/30'
                              : 'bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30'
                          }`}
                        >
                          {togglingMemberType === player.id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (player.member_type || 'membro') === 'membro' ? (
                            <Users className="h-3 w-3" />
                          ) : (
                            <User className="h-3 w-3" />
                          )}
                          {(player.member_type || 'membro') === 'membro' ? 'Membro' : 'Convidado'}
                        </button>
                      </div>
                    )}
                    
                    {/* Member Type Badge - Non-admin */}
                    {evaluator?.role !== 'admin' && (player.member_type === 'convidado') && (
                      <div className="flex justify-center mt-3">
                        <Badge className="bg-amber-500/20 text-amber-400 border border-amber-500/30 text-xs">
                          Convidado
                        </Badge>
                      </div>
                    )}
                    
                    {/* Stats Section */}
                    {playerAverages[player.id] && playerAverages[player.id].total_evaluations > 0 && (
                      <div className="mt-4 pt-4 border-t border-border">
                        <div className="grid grid-cols-3 gap-2">
                          {/* Média */}
                          <div className="text-center">
                            <div className="flex items-center justify-center gap-1 mb-1">
                              <TrendingUp className="h-3 w-3 text-green-400" />
                            </div>
                            <p className="text-xl font-black text-foreground" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                              {(() => {
                                const avgs = playerAverages[player.id].averages;
                                const values = Object.values(avgs).filter(v => v > 0);
                                return values.length > 0 ? (values.reduce((a, b) => a + b, 0) / values.length).toFixed(1) : "0.0";
                              })()}
                            </p>
                            <p className="text-[10px] text-muted-foreground uppercase">Média</p>
                          </div>
                          
                          {/* Ataque */}
                          <div className="text-center">
                            <div className="flex items-center justify-center gap-1 mb-1">
                              <Swords className="h-3 w-3 text-red-400" />
                            </div>
                            <p className="text-xl font-black text-foreground" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                              {playerAverages[player.id].averages?.ataque?.toFixed(1) || "0.0"}
                            </p>
                            <p className="text-[10px] text-muted-foreground uppercase">Ataque</p>
                          </div>
                          
                          {/* Defesa */}
                          <div className="text-center">
                            <div className="flex items-center justify-center gap-1 mb-1">
                              <Shield className="h-3 w-3 text-blue-400" />
                            </div>
                            <p className="text-xl font-black text-foreground" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                              {playerAverages[player.id].averages?.defesa?.toFixed(1) || "0.0"}
                            </p>
                            <p className="text-[10px] text-muted-foreground uppercase">Defesa</p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* No evaluations message */}
                    {(!playerAverages[player.id] || playerAverages[player.id].total_evaluations === 0) && (
                      <div className="mt-4 pt-4 border-t border-border text-center">
                        <p className="text-xs text-muted-foreground">Sem avaliações</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* Make Evaluator Modal */}
      <Dialog open={makeEvaluatorOpen} onOpenChange={setMakeEvaluatorOpen}>
        <DialogContent className="bg-slate-900 border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <UserCheck className="h-5 w-5 text-green-500" />
              Tornar Avaliador/Técnico
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-400">
              Transforme <strong className="text-white">{selectedPlayerForEvaluator && getPlayerName(selectedPlayerForEvaluator)}</strong> em um avaliador ou técnico do sistema.
            </p>
            
            <div className="space-y-2">
              <Label className="text-xs text-slate-400">Tipo de Acesso</Label>
              <Select value={evaluatorRole} onValueChange={setEvaluatorRole}>
                <SelectTrigger className="bg-slate-800/50 border-white/10">
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="avaliador">Avaliador</SelectItem>
                  <SelectItem value="tecnico">Técnico</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-xs text-slate-400">Email</Label>
              <Input
                type="email"
                placeholder="email@exemplo.com"
                value={evaluatorEmail}
                onChange={(e) => setEvaluatorEmail(e.target.value)}
                className="bg-slate-800/50 border-white/10"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs text-slate-400">Senha</Label>
              <Input
                type="password"
                placeholder="Defina uma senha"
                value={evaluatorPassword}
                onChange={(e) => setEvaluatorPassword(e.target.value)}
                className="bg-slate-800/50 border-white/10"
              />
            </div>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setMakeEvaluatorOpen(false)} className="flex-1">
              Cancelar
            </Button>
            <Button 
              onClick={handleMakeEvaluator} 
              disabled={isMakingEvaluator || !evaluatorEmail || !evaluatorPassword}
              className="flex-1 bg-green-600 hover:bg-green-700"
            >
              {isMakingEvaluator ? "Criando..." : `Tornar ${evaluatorRole === 'tecnico' ? 'Técnico' : 'Avaliador'}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
        </>
      )}
    </PageLayout>
  );
}
